/**
 * LinkedInPostGate.jsx — the Home dashboard LinkedIn rotation surface for the
 * day's assigned team member. Sits next to the gratitude control, before
 * Check Out. Opens a dialog with an AI post generator (humanised, no em
 * dashes / buzzwords), an optional project photo, and a Submit for Approval
 * action. Once a founder approves, the assignee gets a Copy button plus an
 * "I've posted it" confirmation. Check Out stays disabled until a post is
 * submitted (status pending/approved/posted).
 */
import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tag } from "@/components/Layout";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Linkedin, Sparkles, Loader2, Copy, Check, AlertTriangle, Clock, Image as ImageIcon, X, CheckCircle2 } from "lucide-react";

const SEED = "Today at Analogy I'm working on ";

export const LinkedInPostGate = ({ data, onChanged }) => {
  const post = data?.my_post || null;
  const status = post?.status || null;

  const [open, setOpen] = useState(false);
  const [text, setText] = useState(post?.content || SEED);
  const [imageUrl, setImageUrl] = useState(post?.image_url || "");
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [markingPosted, setMarkingPosted] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data: res } = await api.post("/linkedin/generate", { prompt: text });
      setText(res.content || "");
      toast.success("Draft ready — tweak it, then submit for approval");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be 5 MB or smaller"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/uploads/linkedin-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(res.url);
      toast.success("Photo attached");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (text.trim().length < 20) { toast.error("Write a bit more before submitting"); return; }
    setSubmitting(true);
    try {
      await api.post("/linkedin/submit", { content: text, image_url: imageUrl || null });
      toast.success("Sent to the founder for approval");
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(post?.content || "");
      setCopied(true);
      toast.success("Copied — paste it into LinkedIn");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy"); }
  };

  const markPosted = async () => {
    setMarkingPosted(true);
    try {
      await api.post(`/linkedin/${post.id}/posted`);
      toast.success("Nice — marked as posted");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMarkingPosted(false); }
  };

  const openEditor = () => {
    setText(post?.content || SEED);
    setImageUrl(post?.image_url || "");
    setOpen(true);
    api.get("/linkedin/topic-suggestions")
      .then(({ data }) => setSuggestions(data?.suggestions || []))
      .catch(() => setSuggestions([]));
  };

  // --- Compact status control shown in the sticky header ---
  let trigger;
  if (status === "posted") {
    trigger = (
      <Tag tone="green" data-testid="linkedin-posted">
        <CheckCircle2 size={11} className="inline mr-1 -mt-0.5" />LinkedIn: posted
      </Tag>
    );
  } else if (status === "approved") {
    trigger = (
      <div className="flex items-center gap-1.5">
        <Button
          size="sm" variant="outline" onClick={copy}
          className="rounded-sm h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="linkedin-copy-btn"
        >
          {copied ? <Check size={13} className="mr-1.5" /> : <Copy size={13} className="mr-1.5" />}
          Approved · Copy
        </Button>
        <Button
          size="sm" onClick={markPosted} disabled={markingPosted}
          className="rounded-sm h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="linkedin-mark-posted-btn"
        >
          {markingPosted ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={13} className="mr-1.5" />}
          I've posted it
        </Button>
      </div>
    );
  } else if (status === "pending") {
    trigger = (
      <Tag tone="yellow" data-testid="linkedin-pending">
        <Clock size={11} className="inline mr-1 -mt-0.5" />LinkedIn: awaiting approval
      </Tag>
    );
  } else if (status === "needs_edit") {
    trigger = (
      <Button
        size="sm" onClick={openEditor}
        className="rounded-sm h-9 bg-rose-600 hover:bg-rose-700 text-white"
        data-testid="linkedin-edit-btn"
      >
        <AlertTriangle size={13} className="mr-1.5" />Edits requested
      </Button>
    );
  } else {
    trigger = (
      <Button
        size="sm" onClick={openEditor}
        className="rounded-sm h-9 bg-[#0A66C2] hover:bg-[#084e96] text-white"
        data-testid="linkedin-write-btn"
      >
        <Linkedin size={13} className="mr-1.5" />Write LinkedIn post
      </Button>
    );
  }

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="linkedin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Linkedin size={16} className="text-[#0A66C2]" /> You're on LinkedIn duty today
            </DialogTitle>
            <DialogDescription>
              Write a line about what you're working on, hit Generate for a humanised draft,
              add a project photo if you like, then submit it for the founder to approve.
            </DialogDescription>
          </DialogHeader>

          {status === "needs_edit" && post?.edit_note && (
            <div className="rounded-sm border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="linkedin-edit-note">
              <b>Edit requested:</b> {post.edit_note}
            </div>
          )}

          {suggestions.length > 0 && (
            <div data-testid="linkedin-suggestions">
              <div className="text-[11px] font-medium text-slate-500 mb-1.5">Need an idea? Tap one to start:</div>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setText(s)}
                    className="rounded-full border border-slate-200 bg-slate-50 hover:bg-[#0A66C2]/10 hover:border-[#0A66C2]/40 px-2.5 py-1 text-[11px] text-slate-600 transition-colors text-left"
                    data-testid={`linkedin-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            maxLength={4000}
            placeholder="What are you working on at Analogy today?"
            className="rounded-sm text-sm resize-none"
            data-testid="linkedin-textarea"
          />
          <div className="text-[11px] text-slate-400">{text.length}/4000</div>

          {/* Optional project photo */}
          {imageUrl ? (
            <div className="flex items-center gap-2" data-testid="linkedin-image-preview">
              <img src={imageUrl} alt="attachment" className="h-14 w-14 rounded-sm object-cover border border-slate-200" />
              <button
                onClick={() => setImageUrl("")}
                className="text-xs text-rose-600 inline-flex items-center gap-1 hover:underline"
                data-testid="linkedin-remove-image"
              >
                <X size={12} /> Remove photo
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer hover:text-slate-700 w-fit">
              <input type="file" accept="image/png,image/jpeg" hidden onChange={handleFile} data-testid="linkedin-image-input" />
              <ImageIcon size={13} /> {uploading ? "Uploading…" : "Add a project photo (JPG/PNG, optional)"}
            </label>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <Button
              variant="outline" size="sm" onClick={generate} disabled={generating || submitting}
              className="rounded-sm" data-testid="linkedin-generate-btn"
            >
              {generating ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Sparkles size={13} className="mr-1.5" />}
              {generating ? "Writing…" : "Generate with AI"}
            </Button>
            <Button
              size="sm" onClick={submit} disabled={submitting || generating || uploading || text.trim().length < 20}
              className="rounded-sm bg-[#0A66C2] hover:bg-[#084e96] text-white disabled:opacity-50"
              data-testid="linkedin-submit-btn"
            >
              {submitting ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null}
              Submit for approval
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
